import React, { useEffect, useMemo, useState } from "react";
import Card from "../Card/Card";
import Const from "const.json";
import ChartBar from "../Charts/ChartBar/ChartBar";
import ChartLine from "../Charts/ChartLine/ChartLine";
import GroupButtons from "../controls/GroupButtons/GroupButtons";
import { ButtonDictionaryItem } from "types/ButtonDictionaryItem";
import { ChartLineDetails } from "types/Chart";
import { useAppSelector } from "../../redux/hooks";
import {
  GetComplaintAmount,
  GetComplaintsAmountDropDownList,
} from "api/apiService";
import { Item } from "types/Item";
import DropDown from "components/controls/DropDown/DropDown";
import useApi from "hooks/useApi";
import WithApi from "components/ui/WithApi/WithApi";
import ExportToExcel from "components/controls/ExportToExcel/ExportToExcel";
import { toIntSafe } from "utils/utils";

const AmountOfComplaints: React.FC = () => {
  const { period, isContrast } = useAppSelector((state) => state.general);
  const { callApi, data, loading, error } = useApi<Item[]>(GetComplaintAmount);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [optionId, setOptionId] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    if (data) {
      let headers = [], rows = [];
      if (!currentIndex) {
        rows[0] = [];
        data.forEach(x => {
          headers.unshift(x.id);
          rows[0].unshift(x.count);
        });
        rows[0].unshift(Const.amountOfComplaints)
      }
      else {
        Const.hebrewMonthNames.forEach(m => headers.push(m));
        data.reverse().forEach((year, index) => {
          rows.push([year.id]);
          year.itemIds.forEach(amount => rows[index].push(toIntSafe(amount)));
        })
      }
      headers.unshift("");
      setHeaders(headers);
      setRows(rows);
    }
  }, [data, currentIndex]);

  useEffect(() => {
    callApi();
    setOptionId(null);
    setCurrentIndex(0);
  }, [period]);

  useEffect(() => {
    callApi(optionId);
  }, [optionId]);

  const [years, months] = useMemo(() => {
    let years = { barLabelData: [], datasets: [] };
    let months = [];
    if (data) {
      years = getYears(data, isContrast);
      months = getMonths(data);
    }
    return [years, months];
  }, [data, isContrast]);

  const handleSelectOption = (optionId?: string) => setOptionId(optionId);

  const buttonDictionary: ButtonDictionaryItem[] = [
    {
      label: Const.years,
      component: (
        <ChartBar
          ariaLabel={getAriaLabel(Const.years, data)}
          details={years}
          chartBarYAxisHeader={Const.chartBarYAxisHeader}
        />
      ),
    },
    {
      label: Const.months,
      component: (
        <ChartLine
          ariaLabel={getAriaLabel(Const.months, data)}
          labels={Const.monthLabel}
          details={months}
        />
      ),
    },
  ];

  return (
    <div>
      <Card
        header={Const.amountOfComplaints}
        subHeader={Const.numberOfComplaintsReceivedComparedToPreviousYears}
        components={[
          <DropDown
          options={GetComplaintsAmountDropDownList}
          title={Const.selectResponsibleBody}
          placeholder={Const.typeNameOfResponsibleBody}
          onSelect={handleSelectOption}
          />,                                                                                                                                                                                                                                            
          <ExportToExcel headers={headers} data={rows} filename={`${Const.amountOfComplaints} ${currentIndex ? Const.byMonths : Const.byYears}`}/>,
          <WithApi data={data} loading={loading} error={error}>
            <>
              <GroupButtons buttonNames={buttonDictionary} setCurrentIndex={setCurrentIndex} selectedIndex={currentIndex}/>
            </>
          </WithApi>,
        ]}
      />
    </div>
  );
};

export default AmountOfComplaints;

const getColor = (
  length: number,
  isContrast,
  defaultColor = isContrast ? "#ADD8E6" : "#aed3ff"
) =>
  length == 0
    ? null
    : Array(length - 1)
      .fill(defaultColor)
      .concat(isContrast ? "#FFC0CB" : "#1172e4");

const getYears = (data: Item[], isContrast: boolean) => {
  const barLabelData = data.map((year) => [year.id]);
  return {
    barLabelData,
    datasets: [
      {
        data: data.map((year) => year.count),
        backgroundColor: getColor(barLabelData.length, isContrast),
      },
    ],
    plugins: {
      legend: {
        display: false,
      },
    },
  };
};

const getMonths = (data: Item[]): ChartLineDetails[] =>
  data.map((data) => ({ label: data.id, data: data.itemIds }));

const getAriaLabel = (by: string, data: Item[]) => {
  let ariaLabel: string = `${Const.amountOfComplaints} ${Const.by} ${by}. `;
  return (
    ariaLabel +
    data
      ?.map(
        (year) =>
          `${Const.inTheYear} ${year.id} ${year.count} ${Const.complaints}`
      )
      .join(", ")
  );
};
